/**
 * Curated hero images per TMDB genre id (movies + TV).
 * Images: Unsplash — standard assets on images.unsplash.com; Unsplash+ on plus.unsplash.com (Imgix).
 * Each URL is verified (HTTP 200); invalid ids show broken images in the UI.
 */

const qs = "auto=format&fit=crop&w=900&h=506&q=85&ixlib=rb-4.0.3";

const IMG = (photoPath: string) =>
  `https://images.unsplash.com/${photoPath}?${qs}`;

/** Unsplash+ / Getty premium assets (404 on images.unsplash.com) */
const IMG_PLUS = (photoPath: string) =>
  `https://plus.unsplash.com/${photoPath}?${qs}`;

/** Default when API returns an unknown id */
export const GENRE_IMAGE_FALLBACK = IMG("photo-1489599849927-2ee91cede3ba");

/**
 * TMDB genre id → image URL.
 * @see https://developer.themoviedb.org/reference/genre-movie-list
 * @see https://developer.themoviedb.org/reference/genre-tv-list
 */
export const GENRE_HERO_IMAGE_BY_ID: Record<number, string> = {
  // Shared / movie
  // https://unsplash.com/photos/riot-police-in-body-armors-twisting-man-into-uncomfortable-position-while-confronting-crowd-in-city-Prn52lmzDo8
  28: IMG_PLUS("premium_photo-1683134559962-1f316af881d0"), // Action (Unsplash+)
  // https://unsplash.com/photos/running-man-on-bridge-uq2E2V4LhCY
  12: IMG("photo-1528543606781-2f6e6857f318"), // Adventure
  // https://unsplash.com/photos/yellow-and-black-robot-toy-mou0S7ViElQ
  16: IMG("photo-1593085512500-5d55148d6f0d"), // Animation
  // https://unsplash.com/photos/man-in-black-shirt-singing-on-stage-NBRNK4XC1k8
  35: IMG("photo-1611956425642-d5a8169abd63"), // Comedy
  // https://unsplash.com/photos/a-piece-of-paper-sitting-on-the-ground-next-to-a-bottle-of-water-crZcH0R_Ew4
  80: IMG_PLUS("premium_photo-1673543763969-1d54002352d0"), // Crime (Unsplash+)
  99: IMG("photo-1455390582262-044cdead277a"), // Documentary
  18: IMG("photo-1503095396549-807759245b35"), // Drama
  10751: IMG("photo-1511895426328-dc8714191300"), // Family
  // https://unsplash.com/photos/reflection-of-a-castle-surrounded-with-fogs-B5PNmw5XSpk
  14: IMG("photo-1518709268805-4e9042af9f23"), // Fantasy
  // https://unsplash.com/photos/colosseum-in-rome-italy-long-exposure-shot-the-rome-colosseum-was-built-in-the-time-of-ancient-rome-in-the-city-center-it-is-the-main-travel-destination-and-tourist-attraction-of-italy-FWFTOJTRih8
  36: IMG_PLUS("premium_photo-1661963952208-2db3512ef3de"), // History (Unsplash+)
  // https://unsplash.com/photos/person-behind-fog-glass-ZC0EbdLC8G0
  27: IMG("photo-1504701954957-2010ec3bcec1"), // Horror
  10402: IMG("photo-1511379938547-c1f69419868d"), // Music
  // https://unsplash.com/photos/grayscale-photography-of-foggy-forest-ZF3WWNGti5Q
  9648: IMG("photo-1531312656382-3764c4c2a264"), // Mystery
  // https://unsplash.com/photos/woman-in-colorful-dress-on-a-flower-decorated-balcony-Yfoxi6D7QdI
  10749: IMG("photo-1769311698182-753ea8d1eda0"), // Romance
  878: IMG("photo-1446776811953-b23d57bd21aa"), // Science Fiction
  10770: IMG("photo-1522869635100-9f4c5e86aa37"), // TV Movie
  // https://unsplash.com/photos/a-man-with-long-hair-and-a-tie-standing-in-a-dark-room-0CotaDzHVM0
  53: IMG("photo-1635593083614-a2bac25c2ddb"), // Thriller
  // https://unsplash.com/photos/group-of-soldiers-in-uniform-moving-through-forest-bushes-while-performing-military-reconnaissance-operation-IdtzzG9oxHo
  10752: IMG_PLUS("premium_photo-1683133515999-6cc3f023c714"), // War (Unsplash+)
  37: IMG("photo-1500530855697-b586d89ba3ee"), // Western

  // TV-only (typical TMDB ids)
  10759: IMG("photo-1516979187457-637abb4f9353"), // Action & Adventure
  // https://unsplash.com/photos/kids-in-spiderman-and-captain-america-costumes-Do1GQljlNk8
  10762: IMG("photo-1519340241574-2cec6aef0c01"), // Kids
  // https://unsplash.com/photos/a-red-breaking-news-sign-surrounded-by-white-cubes-qxio376IXao
  10763: IMG_PLUS("premium_photo-1688561384438-bfa9273e2c00"), // News (Unsplash+)
  10764: IMG("photo-1516035069371-29a1b244cc32"), // Reality
  10765: IMG("photo-1614728894747-a83421e2b9c9"), // Sci-Fi & Fantasy
  10766: IMG("photo-1522337360788-8b13dee7a37e"), // Soap
  10767: IMG("photo-1589829085413-56de8ae18c73"), // Talk
  10768: IMG("photo-1529107386315-e1a2ed48a620"), // War & Politics
};

export function getGenreHeroImage(genreId: number): string {
  return GENRE_HERO_IMAGE_BY_ID[genreId] ?? GENRE_IMAGE_FALLBACK;
}
